<template>
  <div>
    <section>
      <b-col class="d-flex justify-content-between px-0 py-50">
        <b-button
          v-b-modal.modal-create-bid-strategy
          variant="primary"
          :disabled="isLoading"
          size="sm"
        >
          <GjIcon
            class="mr-50"
            name="Plus"
          />
          {{ $tk('ClientPortal.Admin.CreateBidStrategySubtypeMapping') }}
        </b-button>
      </b-col>
      <b-card
        class="my-50"
        no-body
      >
        <Table
          :data="bids"
          :columns="bidColumns"
          :is-loading="isLoading"
          type-of-table="bidStrategies"
          :enable-pagination="false"
          :enable-sort="false"
        />

        <b-modal
          id="modal-delete-bid-strategy"
          :ok-title="$tk('ClientPortal.Form.Actions.Delete')"
          :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
          ok-variant="danger"
          cancel-variant="outline-secondary"
          centered
          size="md"
          :title="$tk('ClientPortal.Admin.DeleteBidStrategySubtypeMapping')"
          no-enforce-focus
          @ok="deleteBidStrategySubtypeMapping"
        >
          <span>{{ $tk('ClientPortal.Admin.AreYouSureYouWantToDeleteBidStrategySubtypeMapping') }}</span>
        </b-modal>

        <b-modal
          id="modal-create-bid-strategy"
          :ok-title="$tk('ClientPortal.Form.Actions.Save')"
          :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
          centered
          no-close-on-backdrop
          cancel-variant="outline-secondary"
          size="md"
          :title="$tk('ClientPortal.Admin.CreateBidStrategySubtypeMapping')"
          no-enforce-focus
          :ok-disabled="saving || isFormComplete()"
          @show="resetBidStrategySubtypeMapping"
          @ok="save"
        >
          <b-form-group
            :label="$tk('ClientPortal.Admin.BidStrategy')"
            label-size="xs"
            :invalid-feedback="errors['BidStrategy']"
            :state="errors['BidStrategy'] == null ? null : false"
          >
            <v-select
              v-model="bidStrategySubtypeMapping.bidStrategyId"
              :clearable="false"
              label="name"
              :placeholder="$tk('ClientPortal.Admin.SelectBidStrategy')"
              :options="bidStrategies"
              :reduce="bid => bid.id"
            />
          </b-form-group>
          <b-form-group
            :label="$tk('ClientPortal.Admin.AdSubtype')"
            label-size="xs"
            :invalid-feedback="errors['AdSubtype']"
            :state="errors['AdSubtype'] == null ? null : false"
          >
            <v-select
              v-model="bidStrategySubtypeMapping.adSubtypeId"
              :clearable="false"
              label="name"
              :placeholder="$tk('ClientPortal.Admin.SelectAdSubtype')"
              :options="adSubtypes"
              :reduce="subtype => subtype.id"
            />
          </b-form-group>
          <b-form-group
            :label="$tk('ClientPortal.Admin.MinimumBidValue')"
            label-size="xs"
            class="mb-0"
            :invalid-feedback="errors['MinimumBidValue']"
          >
            <b-form-input
              v-model="bidStrategySubtypeMapping.minimumBidValue"
              size="sm"
              type="number"
              :state="errors['MinimumBidValue'] == null ? null : false"
            />
          </b-form-group>
          <b-form-group
            :label="$tk('ClientPortal.Admin.MaximumBidValue')"
            label-size="xs"
            class="mb-0"
            :invalid-feedback="errors['MaximumBidValue']"
          >
            <b-form-input
              v-model="bidStrategySubtypeMapping.maximumBidValue"
              size="sm"
              type="number"
              :state="errors['MaximumBidValue'] == null ? null : false"
            />
          </b-form-group>
        </b-modal>
      </b-card>
    </section>
  </div>
</template>

<script>
import { VBModal } from 'bootstrap-vue'
import {
  computed,
  onMounted,
  ref,
  reactive,
} from '@vue/composition-api'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import Table from '@/components/Table.vue'
import notification from '@/mixins/toastification'
import BidStrategiesService from '@/api/admin/bidStrategies.service'
import store from '@/store'
import translation from '@/mixins/translate'
import vSelect from 'vue-select'

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    // Bootstrap Components
    GjIcon,
    Table,
    vSelect,
  },
  mixins: [notification],
  setup() {
    const bidColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.BidStrategy.Name'),
        field: 'bidStrategy',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.BidStrategy.AdSubtype'),
        field: 'adSubtype',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.BidStrategy.MinimumBidValue'),
        field: 'minimumBidValue',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.BidStrategy.MaximumBidValue'),
        field: 'maximumBidValue',
      },
    ]
    const isLoading = ref(false)
    const bids = computed(() => store.getters.getBids)
    const adSubtypes = ref([])
    const errors = ref({})
    const bidStrategies = ref([])
    const saving = ref(false)
    const bidStrategySubtypeMapping = reactive({
      bidStrategyId: null,
      adSubtypeId: null,
      minimumBidValue: null,
      maximumBidValue: null,
    })

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getBids')
      adSubtypes.value = store.getters.getAdSubtypes
      bidStrategies.value = store.getters.getBidStrategies
      isLoading.value = false
    })

    const resetBidStrategySubtypeMapping = () => {
      bidStrategySubtypeMapping.bidStrategyId = null
      bidStrategySubtypeMapping.adSubtypeId = null
      bidStrategySubtypeMapping.minimumBidValue = null
      bidStrategySubtypeMapping.maximumBidValue = null
      errors.value = {}
    }

    const deleteBidStrategySubtypeMapping = async () => {
      const bidStrategySubtypeMappingModel = store.getters.getDeleteBidStrategySubtype
      try {
        const response = await BidStrategiesService.delete(bidStrategySubtypeMappingModel)
        if (response.saved) {
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getBids')
      }
    }
    const isFormComplete = () => !(bidStrategySubtypeMapping.bidStrategyId
              && bidStrategySubtypeMapping.adSubtypeId
              && bidStrategySubtypeMapping.minimumBidValue)

    const save = async bvModalEvt => {
      bvModalEvt.preventDefault()
      saving.value = true
      try {
        const response = await BidStrategiesService.createBid(bidStrategySubtypeMapping)
        if (response.saved) {
          bvModalEvt.vueTarget.hide('modal-create-bid-strategy')
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
          errors.value = response.errors
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        saving.value = false
        await store.dispatch('getBids')
      }
    }

    return {
      isLoading,
      bids,
      adSubtypes,
      bidStrategies,
      errors,
      deleteBidStrategySubtypeMapping,
      bidStrategySubtypeMapping,
      resetBidStrategySubtypeMapping,
      save,
      isFormComplete,
      saving,
      bidColumns,
    }
  },
}
</script>
